import {defineStore} from 'pinia'
import {
    getPaceTypeLabel, isString, ModelType,
    parseRankMetricValue,
    Participation,
    ParticipationClassRankMode, ParticipationClassType, ParticipationInputState,
    ParticipationStatus,
} from "@spoferan/spoferan-ts-core";
import {useEventStore} from "./event";

type ParticipationAuthPermissions = {
    is_initialized: boolean, // Whether the initial permissions have been loaded
    display_name: null|string,
    first_name: null|string,
    can_view: boolean, // Whether the authenticated user can view sensitive information like the participation's name even if it's private
    can_edit: boolean,
    can_cancel: boolean,
    can_delete: boolean,
    currentAttempt: {
        _key: number,
        attempt: number,
        can_virtual_enter: boolean, // Whether the authenticated user can enter the result tracking tracker type selection for the current attempt
        can_start: boolean, // Whether the authenticated user can start the result tracking or live tracking of the participation for the current attempt
        virtual_start_state: boolean|string,
    },
    can_update_results: boolean, // Whether the authenticated user can manually update the participation results
    can_rate: boolean, // Whether the authenticated user can rate the event
    has_rated: boolean, // Whether the authenticated user has rated the event
    has_liked: boolean, // Whether the authenticated user has liked the participation
    is_participant: boolean, // Whether the authenticated user is the participant or a representative of the participant, e.g., a parent
    is_creator: boolean, // Whether the authenticated user signed up the participation but is not the participant
    certificates: {}[], // The certificates of the participation which the user (participant) can download
    order: {[key: string]: any}|null,
    nextStartPrice: {[key: string]: any}|null,
    license_number_input: ParticipationInputState,
    license_number: string|null,
    register_info: string|null,
    participant_info: string|null,
    order_id: number|null,
    inputs: {}[],
    contact: null|{email: string|null, phone: string|null, emergency_phone: string|null, full_address: string|null},
    team_join_code: string|null,
};

const defaultAuthPermissions = {
    is_initialized: false,
    display_name: null,
    first_name: null,
    can_view: false,
    can_edit: false,
    can_cancel: false,
    can_delete: false,
    currentAttempt: {
        _key: 0,
        attempt: 1,
        can_virtual_enter: false,
        can_start: false,
        virtual_start_state: false,
    },
    can_update_results: false,
    can_rate: false,
    has_rated: false,
    has_liked: false,
    is_participant: false,
    is_creator: false,
    certificates: [],
    order: null,
    nextStartPrice: null,
    license_number_input: ParticipationInputState.Disabled,
    license_number: null,
    register_info: null,
    participant_info: null,
    order_id: null,
    inputs: [],
    contact: null,
    team_join_code: null,
} as ParticipationAuthPermissions;

export const useParticipationStore = defineStore('participation', {
    state: () => ({
        /**
         * The current participation details to show
         */
        participation: null as Participation|null,

        /**
         * The participation token the user used to authorized access for the participation.
         * Required to let the user act as participant without being authenticated.
         */
        participationToken: false as string|false,

        /**
         * The auth token that is required to link the user profile of the participation to an account.
         */
        userToken: undefined as string|undefined,

        authPermissions: {...defaultAuthPermissions} as ParticipationAuthPermissions,

        isCheckinCodeModalVisible: false,
        checkinCode: null,

        isUpdateResultsModalVisible: false,
        updateResultsData: {},
    }),

    getters: {
        hasValidParticipationToken: (state) => {
            return !!state.participationToken;
        },

        editable: (state) => {
            return state.authPermissions.can_edit;
        },

        isParticipantVisiting: (state) => {
            return state.authPermissions.is_participant;
        },

        isParticipantOrCreatorVisiting: (state) => {
            return state.isParticipantVisiting || state.authPermissions.is_creator;
        },

        canViewSensitiveInformation: (state) => {
            return state.isParticipantOrCreatorVisiting || state.authPermissions.can_view;
        },

        hasStarted: (state) => {
            return state.hasFinished || [ParticipationStatus.Active, ParticipationStatus.CheckedIn, ParticipationStatus.PreStart].includes(state.participation.status.label);
        },

        isCanceled: (state) => {
            return [ParticipationStatus.Canceled].includes(state.participation.status.label);
        },

        hasFinished: (state) => {
            return state.participation && state.participation.status && [ParticipationStatus.Ranked, ParticipationStatus.TempRanked, ParticipationStatus.NotFinished, ParticipationStatus.Disqualified, ParticipationStatus.NotStarted].indexOf(state.participation.status.label) > -1;
        },

        isActive: (state) => {
            return state.participation && state.participation.status.label === ParticipationStatus.Active;
        },

        hasEnabledAppTracking: (state) => {
            return state.participation.participationClass?.rank_mode === ParticipationClassRankMode.App || state.participation.is_manual
        },

        // Whether the participation has tracking data that might identify the participant and should not be displayed publicly
        hasSensitiveTracking: (state) => {
            // Live participations only display the current location and not the start / finish location
            return state.hasFinished && state.participation && state.participation.participationClass.type === ParticipationClassType.VirtualEvent1;
        },

        maxAttempts: (state) => {
            return state.participation?.participationClass.virtualSettings ? state.participation.participationClass.virtualSettings.max_attempts : 1;
        },

        participationPaceLabel: (state) => {
            return state.participation?.participationClass?.pace_type ? getPaceTypeLabel(state.participation.participationClass.pace_type) : null;
        },

        canStartParticipation: (state) => {
            return state.authPermissions.currentAttempt.can_start;
        },

        // The message to display when the participation could be started but is currently not possible due to time settings
        startParticipationFailureMessage: (state): string|undefined => {
            return isString(state.authPermissions.currentAttempt.virtual_start_state) ? state.authPermissions.currentAttempt.virtual_start_state : undefined;
        }
    },

    actions: {

        parseRankValue(rankValue, rankMetric = null, extended = false) {
            rankMetric = rankMetric ? rankMetric : (this.participation.participationClass && this.participation.participationClass.rank_metric ? this.participation.participationClass.rank_metric : null)

            return parseRankMetricValue(rankMetric, rankValue, extended);
        },

        async refreshParticipation(cacheBuster: string|undefined = undefined) {
            const eventStore = useEventStore();
            const route = useRoute();

            if (!route.params.participation) {
                this.participation = null;
                return;
            }

            const {data} = await useApiFetch(`/v1/view/events/${eventStore.event.id}/participations/${route.params.participation}`, {
                guest: true,
                params: {
                    ts: cacheBuster
                }
            });

            this.participation = data;
        },

        async refreshAuthPermissions() {
            const route = useRoute();
            const {$apiFetch} = useNuxtApp();

            const {data} = await $apiFetch('/v1/auth/permissions', {
                params: {
                    model_type: ModelType.Participation,
                    model_id: route.params.participation, // Use route param to refresh auth permissions asap for page redirects
                    token: this.participationToken
                }
            });
            this.authPermissions = {
                is_initialized: true,
                ...data
            };
        },

        showCheckinCodeModal(code: string) {
           this.checkinCode = code;
           this.isCheckinCodeModalVisible = true;
        },

        showUpdateResultsModal() {
            this.updateResultsData = {
                status: this.participation.status.label === ParticipationStatus.Registered ? ParticipationStatus.Ranked : this.participation.status.label,
                result_info: this.participation.result_info,
                starter_number: this.participation.starter_number,
                time: this.participation.time,
                distance: this.participation.distance,
                points: this.participation.points,
                altitude: this.participation.altitude,
                rounds: this.participation.rounds,
                overall_rank: this.participation.ranks?.find((rank: { template: string|null }) => rank.template === 'overall')?.rank,
                gender_rank: this.participation.ranks?.find((rank: { template: string|null }) => rank.template === 'gender')?.rank,
                age_class_rank: this.participation.ranks?.find((rank: { template: string|null }) => rank.template === 'age_class')?.rank,
            };
            this.isUpdateResultsModalVisible = true;
        },

        updateCacheBuster() {
            const {$date} = useNuxtApp();

            this.participation.cache_buster = $date().unix();
        }
    }
});
